import React, { lazy, Suspense, useState } from "react";
import {
	BrowserRouter,
	HashRouter,
	Route,
	Routes,
	Navigate
} from "react-router-dom";
import { isElectron } from "./utils";
import PaymentModal from "components/Layout/Header/paymentModal";
import { Layout, Modal, Spinner, BlockedAccountBanner } from "components";
import App from "./App";
import ScrollTop from "./hoc/ScrollTop";
import get from "lodash/get";
import MainOrganizationDashboard from "./pages/Main/Dashboard";
import { useSelector } from "react-redux";
import ImportCategories from "./pages/Categories/ImportCategories";
import StorageIncomes from "./pages/Main/Storage/incomes";
import MainOrganizationOrders from "./pages/Main/Reports/orders";
import MainOrganizationReportDishes from "./pages/Main/Reports/dishes";
import { ChequeBuilder } from "pages/cheque-builder";
import BarcodeConstructor from "pages/BarcodeConstructor";
import BarcodeConstructorEditor from "pages/BarcodeConstructor/create";
import config from "config";
import { NewSettings } from "pages/NewSettings/newIndex";

const TechHelp = lazy(() => import("./pages/TechHelp/index"));
const Profile = lazy(() => import("./pages/Profile"));
const Unlock = lazy(() => import("./pages/Auth/Unlock"));
const Login = lazy(() => import("./pages/Auth/Login"));
const SignUp = lazy(() => import("./pages/Auth/SignUp"));
const PhonConfirm = lazy(() => import("./pages/Auth/PhoneConfirm"));
const Forgot = lazy(() => import("./pages/Auth/Forgot"));
const Logout = lazy(() => import("./pages/Auth/Logout"));
const CashRegister = lazy(() => import("./pages/CashRegister"));
const Users = lazy(() => import("./pages/Users"));
const UsersAttendance = lazy(() => import("./pages/Users/Attendance"));
const UsersLogs = lazy(() => import("./pages/Users/Logs"));
const UserAttendance = lazy(() =>
	import("./pages/Users/Attendance/AttendaceById")
);

const Counterparties = lazy(() => import("./pages/Counterparties"));
const Categories = lazy(() => import("./pages/Categories"));

const SettingsProductInfo = lazy(() => import("./pages/Settings/product"));
const SettingsProfile = lazy(() => import("./pages/Settings/profile"));
const Places = lazy(() => import("./pages/Places"));
const PlacesPricingByTime = lazy(() =>
	import("./pages/Places/PlacesPricingByTime")
);
const Tables = lazy(() => import("./pages/Tables"));
const Currencies = lazy(() => import("./pages/Currencies"));

const HandbookPaymentTypes = lazy(() =>
	import("./pages/Handbook/PaymentTypes")
);
const ImportHandbookPaymentTypes = lazy(() =>
	import("./pages/Handbook/PaymentTypes/ImportHandbookPaymentTypes")
);
const HandbookUnits = lazy(() => import("./pages/Handbook/Units"));
const Printers = lazy(() => import("./pages/Printers"));
const Store = lazy(() => import("./pages/Store"));

const Dish = lazy(() => import("./pages/Dish"));
const DishImports = lazy(() => import("./pages/Dish/DishImports"));
const DishCreate = lazy(() => import("./pages/Dish/create"));
const DishUpdate = lazy(() => import("./pages/Dish/update"));
const DishAndTelegramMenus = lazy(() => import("./pages/DishAndTelegramMenus"));

const Products = lazy(() => import("./pages/Storage/Products"));
const ProductsCreate = lazy(() => import("./pages/Storage/Products/components/CreateSimple"));
const ProductsCreateVariative = lazy(() => import("./pages/Storage/Products/components/CreateVariative"));
const ProductsCreateSet = lazy(() => import("./pages/Storage/Products/components/CreateSet"));
const ProductsCreateService = lazy(() => import("./pages/Storage/Products/components/CreateService"));
const ProductsUpdate = lazy(() => import("./pages/Storage/Products/components/Update"));
const ProductsUpdateVariative = lazy(() => import("./pages/Storage/Products/components/UpdateVariative"));
const ProductsUpdateSet = lazy(() => import("./pages/Storage/Products/components/UpdateSet"));
const ProductsUpdateService = lazy(() => import("./pages/Storage/Products/components/UpdateService"));
const ProductOptions = lazy(() => import("./pages/ProductOptions"));
const ProductOptionsChild = lazy(() => import("./pages/ProductOptionsChild"));
const ProductCategories = lazy(() => import("./pages/ProductCategories"));

const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardNew = lazy(() => import("./pages/DashboardNew"));
const Statistics = lazy(() => import("./pages/Statistics"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Maintenance = lazy(() => import("./pages/Maintenance"));

const Warehouse = lazy(() => import("./pages/Warehouse/List"));
const MainOrganizationWarehouse = lazy(() =>
	import("./pages/Main/Storage/storages")
);
const Storage = lazy(() => import("./pages/Storage/View"));
const ComingsReport = lazy(() =>
	import("./pages/Storage/Comings/comingReport")
);
const ComingReportInner = lazy(() =>
	import("./pages/Storage/Comings/comingReportInner")
);
const Comings = lazy(() => import("./pages/Storage/Comings"));
const ComingsView = lazy(() => import("./pages/Storage/Comings/View"));
const OutComings = lazy(() => import("./pages/Storage/OutComings"));
const OutComingsView = lazy(() => import("./pages/Storage/OutComings/View"));
const Transfer = lazy(() => import("./pages/Storage/Transfers"));
const TransferView = lazy(() => import("./pages/Storage/Transfers/View"));
const Materials = lazy(() => import("./pages/Storage/Materials"));
const MaterialsImports = lazy(() =>
	import("./pages/Storage/Materials/materials-import")
);
const SemiFinishedProducts = lazy(() =>
	import("./pages/Storage/SemiFinishedProducts")
);
const SemiFinishedProductsImport = lazy(() =>
	import("./pages/Storage/SemiFinishedProducts/SemiFinishedProductsImport")
);
const Adjustments = lazy(() => import("./pages/Storage/Adjustments"));
const Consumption = lazy(() => import("./pages/Storage/Consumption"));
const MainOrganizationConsumption = lazy(() =>
	import("./pages/Main/Storage/consumption")
);
const ConsumptionInner = lazy(() =>
	import("./pages/Storage/Consumption/inner")
);
const CategoriesExpense = lazy(() =>
	import("./pages/Finance/CategoriesExpense")
);
const CategoriesIncome = lazy(() => import("./pages/Finance/CategoriesIncome"));
const Transactions = lazy(() => import("./pages/Finance/Transactions"));
const Cashbox = lazy(() => import("./pages/Finance/Cashbox"));

const ReportZ = lazy(() => import("./pages/Reports/reportZ"));
const ReportDebtCredit = lazy(() => import("./pages/Reports/reportDebtCredit"));
const ReportAct = lazy(() => import("./pages/Reports/reportAct"));
const ReportStorage = lazy(() => import("./pages/Reports/reportStorage"));
const ReportCashflow = lazy(() => import("./pages/Reports/reportCashflow"));
const ReportOrders = lazy(() => import("./pages/Reports/reportOrders"));
const ReportOrdersTable = lazy(() =>
	import("./pages/Reports/reportOrdersTable")
);
const ReportDebtCreditDashboard = lazy(() =>
	import("./pages/Reports/reportDebtCredit/dashboard")
);
const MainOrganizationReportOrdersTable = lazy(() =>
	import("./pages/Main/Reports/orders-table")
);
const MainOrganizationReportRemovedDishes = lazy(() =>
	import("./pages/Main/Reports/removed-dishes")
);
const ReportDishes = lazy(() => import("./pages/Reports/reportDishes"));
const ReportWaiter = lazy(() => import("./pages/Reports/reportWaiter"));
const ReportCashier = lazy(() => import("./pages/Reports/reportCashiers"));
const ReportCourier = lazy(() => import("./pages/Reports/reportCourier"));
const MainOrganizationReportWaiter = lazy(() =>
	import("./pages/Main/Reports/waiter")
);
const ReportReturnedDishes = lazy(() =>
	import("./pages/Reports/reportReturnedDishes")
);
const ReportRemovedDishes = lazy(() =>
	import("./pages/Reports/reportRemovedDishes")
);
const McDevices = lazy(() => import("./pages/MenuCast/pages/devices"));
const McPlaylists = lazy(() => import("./pages/MenuCast/pages/playlists"));
const McPlaylistsById = lazy(() => import("./pages/MenuCast/pages/playlist-items-by-id"));
const Banners = lazy(() => import("./pages/Banners"));

const MarketingStocks = lazy(() => import("./pages/Marketing/Stocks"));
const MarketingStocksCreate = lazy(() => import("./pages/Marketing/Stocks/create"));
const MarketingStocksUpdate = lazy(() => import("./pages/Marketing/Stocks/create"));
const Sms = lazy(() => import("./pages/Marketing/Sms"));
const SmsCreate = lazy(() => import("./pages/Marketing/Sms/create"));
const SmsUpdate = lazy(() => import("./pages/Marketing/Sms/create"));
const SmsView = lazy(() => import("./pages/Marketing/Sms/view"));

///// HAMKOR BANK
const HamkorBank = lazy(() => import("./components/Layout/Header/HamkorBank"));
const ServiceOrders = lazy(() => import("./pages/ServiceOrders/"));

const routeConfigs = [
	{ path: "/mc-devices", component: ScrollTop(McDevices), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/mc-playlists", component: ScrollTop(McPlaylists), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/mc-playlists/:id", component: ScrollTop(McPlaylistsById), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/profile", component: ScrollTop(Profile), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/settings/tech-help", component: ScrollTop(TechHelp), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/logout", component: ScrollTop(Logout), access: ["owner"] },
	{ path: "/", component: ScrollTop(Warehouse), access: ["warehouse"] },
	{ path: "/", component: ScrollTop(DashboardNew), access: ["owner", "manager"] },
	{ path: "/statistics", component: ScrollTop(Statistics), access: ["owner", "manager"] },
	{ path: "/settings/printers", component: ScrollTop(Printers), access: ["manager", "owner"] },
	{ path: "/settings/cheque-builder", component: ScrollTop(ChequeBuilder), access: ["manager", "owner"] },
	{ path: "/settings/barcode-constructor/update/:id", component: ScrollTop(BarcodeConstructorEditor), access: ["manager", "owner"] },
	{ path: "/settings/barcode-constructor/create", component: ScrollTop(BarcodeConstructorEditor), access: ["manager", "owner"] },
	{ path: "/settings/barcode-constructor", component: ScrollTop(BarcodeConstructor), access: ["manager", "owner"] },
	{ path: "/settings/new-settings", component: ScrollTop(NewSettings), access: ["manager", "owner"] },
	{ path: "/settings/product", component: ScrollTop(SettingsProductInfo), access: ["manager", "owner"] },
	{ path: "/settings/profile", component: ScrollTop(SettingsProfile), access: ["manager", "owner"] },
	{ path: "/marketing/stocks", component: ScrollTop(MarketingStocks), access: ["manager", "owner"] },
	{ path: "/marketing/stocks/create", component: ScrollTop(MarketingStocksCreate), access: ["manager", "owner"] },
	{ path: "/marketing/stocks/update/:id", component: ScrollTop(MarketingStocksUpdate), access: ["manager", "owner"] },
	{ path: "/marketing/sms", component: ScrollTop(Sms), access: ["manager", "owner"] },
	{ path: "/marketing/sms/create", component: ScrollTop(SmsCreate), access: ["manager", "owner"] },
	{ path: "/marketing/sms/update/:id", component: ScrollTop(SmsUpdate), access: ["manager", "owner"] },
	{ path: "/marketing/sms/view/:id", component: ScrollTop(SmsView), access: ["manager", "owner"] },
	{ path: "/places", component: ScrollTop(Places), access: ["manager", "owner"] },
	{ path: "/places/pricing-by-time/:placeId", component: ScrollTop(PlacesPricingByTime), access: ["manager", "owner"] },
	{ path: "/places/:id/tables", component: ScrollTop(Tables), access: ["manager", "owner"] },
	{ path: "/cash-register", component: ScrollTop(CashRegister), access: ["manager", "cashier"] },
	{ path: "/dashboard", component: ScrollTop(DashboardNew), access: ["owner", "manager"] },
	{ path: "/users", component: ScrollTop(Users), access: ["owner", "manager"] },
	{ path: "/users/logs", component: ScrollTop(UsersLogs), access: ["owner", "manager"] },
	{ path: "/users/attendance", component: ScrollTop(UsersAttendance), access: ["owner", "manager"] },
	{ path: "/users/attendance/:id", component: ScrollTop(UserAttendance), access: ["owner", "manager"] },
	{ path: "/counterparties", component: ScrollTop(Counterparties), access: ["manager", "owner"] },
	{ path: "/categories/:type", component: ScrollTop(Categories), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/categories/:type/import", component: ScrollTop(ImportCategories), access: ["manager", "owner", "warehouse"] },
	{ path: "/reports/debt-credit", component: ScrollTop(ReportDebtCredit), access: ["owner", "manager"] },
	{ path: "/reports/debt-credit/dashboard", exact: true, component: ScrollTop(ReportDebtCreditDashboard), access: ["owner", "manager"] },
	{ path: "/reports/act/:id", component: ScrollTop(ReportAct), access: ["owner", "manager"] },
	{ path: "/reports/storage-material", component: ScrollTop(ReportStorage), access: ["owner", "manager"] },
	{ path: "/reports/cashflow", component: ScrollTop(ReportCashflow), access: ["owner", "manager"] },
	{ path: "/report/orders", component: ScrollTop(ReportOrders), access: ["owner", "manager"] },
	{ path: "/report/orders-table", component: ScrollTop(ReportOrdersTable), access: ["owner", "manager"] },
	{ path: "/report/dishes", component: ScrollTop(ReportDishes), access: ["owner", "manager"] },
	{ path: "/report/returned-dishes", component: ScrollTop(ReportReturnedDishes), access: ["owner", "manager"] },
	{ path: "/report/removed-dishes", component: ScrollTop(ReportRemovedDishes), access: ["owner", "manager"] },
	{ path: "/report/waiter", component: ScrollTop(ReportWaiter), access: ["owner", "manager"] },
	{ path: "/report/cashier", component: ScrollTop(ReportCashier), access: ["owner", "manager"] },
	{ path: "/report/courier", component: ScrollTop(ReportCourier), access: ["owner", "manager"] },
	{ path: "/report/z", component: ScrollTop(ReportZ), access: ["owner", "manager"] },
	{ path: "/handbook/units", component: ScrollTop(HandbookUnits), access: ["manager", "owner"] },
	{ path: "/handbook/payment-types", component: ScrollTop(HandbookPaymentTypes), access: ["manager", "owner"] },
	{ path: "/handbook/payment-types/import", component: ScrollTop(ImportHandbookPaymentTypes), access: ["manager", "owner"] },
	{ path: "/dish-categories", component: ScrollTop(DishAndTelegramMenus), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/dish", component: ScrollTop(Dish), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/dish/imports", component: ScrollTop(DishImports), access: ["manager", "owner", "warehouse"] },
	{ path: "/dish/archive", component: ScrollTop(Dish), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/dish/create", component: ScrollTop(DishCreate), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/dish/:id/update", component: ScrollTop(DishUpdate), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products", component: Products, access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/archive", component: Products, access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/create", component: ScrollTop(ProductsCreate), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/create-variative", component: ScrollTop(ProductsCreateVariative), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/create-set", component: ScrollTop(ProductsCreateSet), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/create-service", component: ScrollTop(ProductsCreateService), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/:id/update", component: ScrollTop(ProductsUpdate), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/:id/update-variative", component: ScrollTop(ProductsUpdateVariative), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/:id/update-set", component: ScrollTop(ProductsUpdateSet), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/products/:id/update-service", component: ScrollTop(ProductsUpdateService), access: ["manager", "owner", "warehouse", "tech-card"] },
	{ path: "/product-params/:id", component: ScrollTop(ProductOptionsChild), access: ["owner", "manager"] },
	{ path: "/product-params", component: ScrollTop(ProductOptions), access: ["owner", "manager"] },
	{ path: "/product-categories", component: ScrollTop(ProductCategories), access: ["owner", "manager"] },
	{ path: "/warehouse", component: ScrollTop(Warehouse), access: ["manager", "owner", "cashier", "warehouse"] },
	{ path: "/storages", component: ScrollTop(Warehouse), access: ["manager", "owner", "cashier", "warehouse"] },
	{ path: "/storages/:id", component: ScrollTop(Storage), access: ["manager", "owner", "cashier", "warehouse"] },
	{ path: "/storage/incomes", component: ScrollTop(ComingsReport), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/incomes/:id", component: ScrollTop(ComingReportInner), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/comings", component: ScrollTop(Comings), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/comings/:id", component: ScrollTop(ComingsView), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/out-comings", component: ScrollTop(OutComings), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/out-comings/:id", component: ScrollTop(OutComingsView), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/transfers", component: ScrollTop(Transfer), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/transfers/:id", component: ScrollTop(TransferView), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/adjustments", component: ScrollTop(Adjustments), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/consumption", component: ScrollTop(Consumption), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/consumption/:id", component: ScrollTop(ConsumptionInner), access: ["manager", "owner", "warehouse"] },
	{ path: "/transactions/categories/expense", component: ScrollTop(CategoriesExpense), access: ["manager", "owner"] },
	{ path: "/transactions/categories/income", component: ScrollTop(CategoriesIncome), access: ["manager", "owner"] },
	{ path: "/transactions", component: ScrollTop(Transactions), access: ["manager", "owner"] },
	{ path: "/cashbox", component: ScrollTop(Cashbox), access: ["manager", "owner"] },
	{ path: "/currencies", component: ScrollTop(Currencies), access: ["manager", "owner"] },
	{ path: "/store", component: ScrollTop(Store), access: ["manager", "owner"] },
	{ path: "/materials", component: ScrollTop(Materials), access: ["owner", "manager", "warehouse", "tech-card"] },
	{ path: "/materials/archive", component: ScrollTop(Materials), access: ["owner", "manager", "warehouse", "tech-card"] },
	{ path: "/semi-finished-products", component: ScrollTop(SemiFinishedProducts), access: ["owner", "manager", "warehouse", "tech-card"] },
	{ path: "/semi-finished-products/import", component: ScrollTop(SemiFinishedProductsImport), access: ["owner", "manager", "warehouse"] },
	{ path: "/semi-finished-products/archive", component: ScrollTop(SemiFinishedProducts), access: ["owner", "manager", "warehouse", "tech-card"] },
	{ path: "/materials/imports", component: ScrollTop(MaterialsImports), access: ["owner", "manager", "warehouse"] },
	{ path: "/banner", component: ScrollTop(Banners), access: ["owner", "manager", "warehouse"] },
	{ path: "/hamkor-bank", component: ScrollTop(HamkorBank), access: ["owner", "manager", "warehouse", "cashier"] },
	{ path: "/service-orders", component: ScrollTop(ServiceOrders), access: ["owner", "manager", "warehouse", "cashier"] },
];

const mainOrganizationRouteConfigs = [
	{ path: "/users", component: ScrollTop(Users), access: ["owner", "manager"] },
	{ path: "/hamkor-bank", component: ScrollTop(HamkorBank), access: ["owner", "manager"] },
	{ path: "/transactions/categories/expense", component: ScrollTop(CategoriesExpense), access: ["manager", "owner"] },
	{ path: "/transactions/categories/income", component: ScrollTop(CategoriesIncome), access: ["manager", "owner"] },
	{ path: "/transactions", component: ScrollTop(Transactions), access: ["manager", "owner"] },
	{ path: "/counterparties", component: ScrollTop(Counterparties), access: ["manager", "owner"] },
	{ path: "/settings/profile", component: ScrollTop(SettingsProfile), access: ["manager", "owner"] },
	{ path: "/settings/tech-help", component: ScrollTop(TechHelp), access: ["owner", "manager", "cashier", "warehouse"] },
	{ path: "/handbook/payment-types", component: ScrollTop(HandbookPaymentTypes), access: ["manager", "owner"] },
	{ path: "/", component: ScrollTop(MainOrganizationDashboard), access: ["owner", "manager"] },
	{ path: "/dish-categories", component: ScrollTop(DishAndTelegramMenus), access: ["manager", "owner", "warehouse"] },
	{ path: "/dish", component: ScrollTop(Dish), access: ["manager", "owner", "warehouse"] },
	{ path: "/dish/archive", component: ScrollTop(Dish), access: ["manager", "owner", "warehouse"] },
	{ path: "/dish/create", component: ScrollTop(DishCreate), access: ["manager", "owner", "warehouse"] },
	{ path: "/dish/:id/update", component: ScrollTop(DishUpdate), access: ["manager", "owner", "warehouse"] },
	{ path: "/materials", component: ScrollTop(Materials), access: ["owner", "manager", "warehouse"] },
	{ path: "/materials/archive", component: ScrollTop(Materials), access: ["owner", "manager", "warehouse"] },
	{ path: "/semi-finished-products", component: ScrollTop(SemiFinishedProducts), access: ["owner", "manager", "warehouse"] },
	{ path: "/semi-finished-products/archive", component: ScrollTop(SemiFinishedProducts), access: ["owner", "manager", "warehouse"] },
	{ path: "/categories/:type", component: ScrollTop(Categories), access: ["manager", "owner", "warehouse"] },
	{ path: "/storages", component: ScrollTop(MainOrganizationWarehouse), access: ["manager", "owner", "cashier", "warehouse"] },
	{ path: "/storages/:id", component: ScrollTop(Storage), access: ["manager", "owner", "cashier", "warehouse"] },
	{ path: "/storage/incomes", component: ScrollTop(StorageIncomes), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/consumption", component: ScrollTop(MainOrganizationConsumption), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/consumption/:id", component: ScrollTop(ConsumptionInner), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/incomes/:id", component: ScrollTop(ComingReportInner), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/comings", component: ScrollTop(Comings), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/adjustments", component: ScrollTop(Adjustments), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/comings/:id", component: ScrollTop(ComingsView), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/transfers", component: ScrollTop(Transfer), access: ["manager", "owner", "warehouse"] },
	{ path: "/storage/transfers/:id", component: ScrollTop(TransferView), access: ["manager", "owner", "warehouse"] },
	{ path: "/reports/debt-credit", component: ScrollTop(ReportDebtCredit), access: ["owner", "manager"] },
	{ path: "/reports/debt-credit/dashboard", exact: true, component: ScrollTop(ReportDebtCreditDashboard), access: ["owner", "manager"] },
	{ path: "/reports/act/:id", component: ScrollTop(ReportAct), access: ["owner", "manager"] },
	{ path: "/reports/storage-material", component: ScrollTop(ReportStorage), access: ["owner", "manager"] },
	{ path: "/reports/cashflow", component: ScrollTop(ReportCashflow), access: ["owner", "manager"] },
	{ path: "/report/orders", component: ScrollTop(MainOrganizationOrders), access: ["owner", "manager"] },
	{ path: "/report/orders-table", component: ScrollTop(MainOrganizationReportOrdersTable), access: ["owner", "manager"] },
	{ path: "/report/dishes", component: ScrollTop(MainOrganizationReportDishes), access: ["owner", "manager"] },
	{ path: "/report/returned-dishes", component: ScrollTop(MainOrganizationReportRemovedDishes), access: ["owner", "manager"] },
	{ path: "/report/waiter", component: ScrollTop(MainOrganizationReportWaiter), access: ["owner", "manager"] },
	{ path: "/report/z", component: ScrollTop(ReportZ), access: ["owner", "manager"] },
	{ path: "/settings/printers", component: ScrollTop(Printers), access: ["manager", "owner"] },
	{ path: "/banner", component: ScrollTop(Banners), access: ["owner", "manager", "warehouse"] },
];

const ownerRoutes = routeConfigs.filter(i => i.access.includes("owner"));
const managerRoutes = routeConfigs.filter(i => i.access.includes("manager"));
const warehouseRoutes = routeConfigs.filter(i => i.access.includes("warehouse"));
const techCardRoutes = routeConfigs.filter(i => i.access.includes("tech-card"));

function renderRoutes(routeList) {
	return routeList.map((route, key) => {
		const Comp = route.component;
		return <Route key={key} path={route.path} element={<Comp />} />;
	});
}

export default function RoutesRoot() {
	const [paymentModal, showPaymentModal] = useState(false);

	const { isMainOrganization, selectedOrgId, maintenanceMode } = useSelector(state => {
		return {
			isMainOrganization: state.auth.is_main_organization,
			selectedOrgId: get(state, "auth.selected_organization.id"),
			maintenanceMode: state.auth.maintenance_mode
		};
	});

	const isTechCardOrganization =
		String(config.TECH_CARD_ORGANIZATION_ID) === String(selectedOrgId);

	const selected_organization = useSelector(state =>
		get(state, "auth.selected_organization.status")
	);

	const routerBasename =
		(import.meta.env.BASE_URL || "/").replace(/\/$/, "") || "/";

	const WebRouter = isElectron() ? HashRouter : BrowserRouter;

	return (
		<WebRouter basename={routerBasename}>
			<App>
				{({ isFetched, isAuthenticated, user }) => {
					if (!isFetched) {
						return (
							<div>
								<Spinner position={"full"} />
							</div>
						);
					}

					if (maintenanceMode && !isAuthenticated) {
						return (
							<Suspense
								fallback={<div className="loading-bar loading-bar--fixed" />}
							>
								<div className="h-full">
									<Maintenance />
								</div>
							</Suspense>
						);
					}

					if (isAuthenticated) {
						return (
							<Layout>
								{selected_organization === 0 ? (
									<>
										<Modal
											size={500}
											isOpen={paymentModal}
											toggle={() => showPaymentModal(false)}
										>
											<PaymentModal {...{ showPaymentModal }} />
										</Modal>

										<BlockedAccountBanner
											onClick={() => showPaymentModal(true)}
										/>
									</>
								) : null}

								<Suspense
									fallback={<div className="loading-bar loading-bar--fixed" />}
								>
									<Routes>
										{(() => {
											if (isTechCardOrganization) {
												return renderRoutes(techCardRoutes);
											}
											if (isMainOrganization) {
												return renderRoutes(mainOrganizationRouteConfigs);
											}
											if (get(user, "role.role") === "owner") {
												return renderRoutes(ownerRoutes);
											}
											if (get(user, "role.role") === "manager") {
												return renderRoutes(managerRoutes);
											}
											if (get(user, "role.role") === "cashier") {
												return renderRoutes(ownerRoutes);
											}
											if (get(user, "role.role") === "warehouse") {
												return renderRoutes(warehouseRoutes);
											}
											return null;
										})()}
										<Route path="*" element={<NotFound />} />
									</Routes>
								</Suspense>
							</Layout>
						);
					}

					return (
						<Suspense
							fallback={<div className="loading-bar loading-bar--fixed" />}
						>
							<Routes>
								<Route path="/" element={<Login />} />
								<Route path="/signup" element={<SignUp />} />
								<Route path="/phone-confirm" element={<PhonConfirm />} />
								<Route path="/forgot" element={<Forgot />} />
								<Route path="/unlock" element={<Unlock />} />
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						</Suspense>
					);
				}}
			</App>
		</WebRouter>
	);
}
